import type { RouteDef } from '@/app/routes'
import type { WrittenPage } from '../sections'

/**
 * `/newsletter` — what the newsletter is, and every issue that has gone out.
 *
 * WHY THIS PAGE EXISTS. An issue is a page on the site plus a short mail (D57),
 * which means every issue has had a permanent address since the sender shipped
 * — and no way to reach it except the mail that announced it. So a reader who
 * joined last month could not read last month's issue, a reader deciding
 * whether to subscribe had nothing to judge it by, and Google saw a set of
 * unlinked orphan pages rather than a series. One index answers all three.
 *
 * NO SUBSCRIBE FORM HERE, deliberately: the footer carries the newsletter box
 * on every page of the site, including this one, so a form in the body would be
 * the same field twice on one screen. The page's job is the archive; the
 * footer's job is the sign-up, and it is already directly under the list.
 *
 * `priority: 0.5` — a hub, worth indexing, below the solution pages that are
 * what somebody is actually searching for.
 */
export const route: RouteDef = {
  id: 'newsletter',
  path: 'newsletter',
  priority: 0.5,
}

export const newsletter: WrittenPage = {
  navLabel: 'Newsletter',
  title: 'The Digitopia Newsletter',
  description:
    'One email a month on digital and AI transformation: what we are seeing, what the data says, and what we would do about it. Every issue is here.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Newsletter',
      headline: 'What we are seeing, once a month.',
      sub: 'One email. No digest of other people’s links.',
      body: [
        'Every issue takes one thing happening in digital and AI transformation and says what the data shows and what we would do about it — written by the people doing the work, not by a marketing calendar.',
        'It goes out once a month, it is one email, and the unsubscribe link works in one click. Sign up at the foot of this page.',
      ],
    },
    {
      kind: 'issues',
      heading: 'Every issue',
      // DELIBERATELY UNDATED. The first draft of this said "goes out this
      // month", which is a commitment on a public page and a deadline the
      // website would have set for the person writing the issue. A page that
      // says nothing about when is honest on any day it is read; one that names
      // a month is wrong from the first of the next one.
      empty:
        'The first issue is being written. Subscribe at the foot of this page and it will be the first thing you get; every issue after it stays here to read.',
    },
  ],
}
